import React from 'react';
import {FlexContainer} from 'app/styles/content';
import styled from 'styled-components';
import {COLORS, SIZES, Typography} from '@fupa/fupa-uikit';
import {getPlayerShortName} from 'app/helpers/helpers';
import {Card, Football, TwoCards, PenaltyMissed} from 'app/components/icons';
import {Timer} from '@mui/icons-material';
import {TransitionGroup} from 'react-transition-group';
import {Collapse} from '@mui/material';

const Container = styled(FlexContainer)`
  padding-top: ${SIZES['8']};
  width: 100%;
  flex-direction: column;
  align-items: center;

  .MuiCollapse-wrapperInner {
    display: flex;
    justify-content: center;
  }
`;

const HomeHighlight = styled(FlexContainer)`
  align-items: center;
  width: 100%;
  text-align: right;
  height: ${SIZES['24']};
  max-width: 30rem;
`;

const AwayHighlight = styled(HomeHighlight)`
  &&& {
    text-align: left;
  }
`;

const Name = styled.div`
  line-height: ${SIZES['16']};
  flex: 1 25%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Event = styled(Name)`
  &&& {
    flex: 1 10%;
  }
`;

const ElementContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${SIZES['16']};
  line-height: ${SIZES['16']};
  flex: 1 10%;
`;

const Free = styled.div`
  flex: 1 45%;
`;

const MatchHighlight = ({highlight, homeTeamSlug}) => {
  let event = `${highlight.homeGoal}:${highlight.awayGoal}`;
  let icon = <Football style={{color: COLORS.darkerGrey, fontSize: 12}} />;
  let name = highlight.primaryRole?.lastName ? getPlayerShortName(highlight.primaryRole) : 'Unbekannt';
  let time = highlight.additionalMinute ? `${highlight.minute}'+${highlight.additionalMinute}` : `${highlight.minute}'`;

  switch (highlight.subtype) {
    case 'penalty_foul':
    case 'penalty_hand':
    case 'penalty_shootout':
      const isPenaltyFail = highlight.type === 'penaltyfail';
      const color = isPenaltyFail ? COLORS.neonRed : COLORS.green;
      const getEventText = {
        penalty_foul: 'FE',
        penalty_hand: 'HE',
        penalty_shootout: 'i.E.',
      };
      event = isPenaltyFail ? getEventText[highlight.subtype] ?? '' : event;
      icon = isPenaltyFail ? <PenaltyMissed style={{fontSize: 12}} /> : <Football style={{color, fontSize: 12}} />;
      break;
    case 'card_yellow_red':
      event = 'G/R';
      icon = <TwoCards style={{fontSize: 12}} />;
      break;
    case 'card_red':
      event = 'Rot';
      icon = <Card style={{color: COLORS.red, fontSize: 12}} />;
      break;
    case 'goal_own_goal':
      name = 'Eigentor';
      break;
    case 'time_penalty_start':
      event = `${highlight.length}'`;
      icon = <Timer style={{color: COLORS.neonRed, fontSize: 12}} />;
      break;
  }

  return highlight.team.slug === homeTeamSlug ? (
    <HomeHighlight>
      <Name>
        <Typography variant='caption1'>{name}</Typography>
      </Name>
      <Event>
        <Typography variant='caption1'>{event}</Typography>
      </Event>
      <ElementContainer home>{icon}</ElementContainer>
      <ElementContainer as={Typography} variant='caption3'>
        {time}
      </ElementContainer>
      <Free />
    </HomeHighlight>
  ) : (
    <AwayHighlight>
      <Free />
      <ElementContainer as={Typography} variant='caption3'>
        {time}
      </ElementContainer>
      <ElementContainer>{icon}</ElementContainer>
      <Event>
        <Typography variant='caption1'>{event}</Typography>
      </Event>
      <Name>
        <Typography variant='caption1'>{name}</Typography>
      </Name>
    </AwayHighlight>
  );
};

const MatchHighlights = ({homeTeamSlug, highlights}) => {
  // highlights are type goal or card
  const filteredHighlights = highlights.filter(highlight => highlight.subtype !== 'card_yellow');

  const displayedHighlights = filteredHighlights.map(highlight =>
    highlight.team ? (
      <Collapse key={highlight.id}>
        <MatchHighlight key={highlight.id} highlight={highlight} homeTeamSlug={homeTeamSlug} />
      </Collapse>
    ) : null
  );

  return (
    <Container>
      <TransitionGroup style={{width: 'inherit'}}>{displayedHighlights}</TransitionGroup>
    </Container>
  );
};

export default MatchHighlights;
